import React from 'react';
import {
  FaServer,
  FaPlay,
  FaPowerOff,
  FaPause,
  FaTools,
  FaSync,
  FaClock,
  FaMicrochip,
  FaMemory,
} from 'react-icons/fa';
import { Card, SectionTitle, UsageBar } from './Primitives';
import VmDistributionChart from './VmDistributionChart';
import {
  formatMemoryMb,
  formatMhz,
  formatUptime,
} from '../utils/format';

/** Card compatta di riepilogo con icona, valore e descrizione. */
function StatCard({ icon: Icon, label, value, accent = 'text-primary' }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/40">
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm dark:bg-gray-800 ${accent}`}>
        <Icon size={16} aria-hidden="true" />
      </span>
      <div className="min-w-0">
        <div className="text-lg font-bold leading-tight text-gray-900 dark:text-white">
          {value}
        </div>
        <div className="truncate text-xs text-gray-500 dark:text-gray-400">
          {label}
        </div>
      </div>
    </div>
  );
}

/** Rende un flag booleano host in una forma leggibile (con ripiego "n/d"). */
function boolLabel(value, { onTrue, onFalse }) {
  if (value === true) return onTrue;
  if (value === false) return onFalse;
  return 'n/d';
}

function usageLabel(percentValue, used, total, formatter) {
  const pct = percentValue == null ? null : `${Math.round(percentValue)}%`;
  const amount =
    used != null && total != null
      ? `${formatter(used)} / ${formatter(total)}`
      : used != null
      ? formatter(used)
      : null;
  return [pct, amount].filter(Boolean).join(' · ') || 'n/d';
}

/**
 * Sezione di riepilogo host: conteggi VM, maintenance mode, riavvio richiesto,
 * uptime, utilizzo CPU/memoria e distribuzione grafica delle VM.
 * Tutti i valori host sono difensivi: se assenti mostrano "n/d".
 */
export default function HostSummary({ summary }) {
  const counts = summary?.counts || {
    total: 0,
    on: 0,
    off: 0,
    suspended: 0,
    unknown: 0,
  };
  const suspendedOrUnknown = (counts.suspended || 0) + (counts.unknown || 0);

  return (
    <Card className="p-5">
      <SectionTitle
        icon={FaServer}
        title="Riepilogo host — ESXi 1"
        subtitle="Stato sintetico dell’host e distribuzione delle macchine virtuali"
      />

      <div className="mt-4 grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* Conteggi VM */}
        <div className="grid grid-cols-2 gap-3 lg:col-span-2">
          <StatCard icon={FaServer} label="VM totali" value={counts.total} accent="text-primary" />
          <StatCard icon={FaPlay} label="VM accese" value={counts.on} accent="text-emerald-500" />
          <StatCard icon={FaPowerOff} label="VM spente" value={counts.off} accent="text-gray-400" />
          <StatCard
            icon={FaPause}
            label="Sospese o sconosciute"
            value={suspendedOrUnknown}
            accent="text-amber-500"
          />
        </div>

        {/* Distribuzione grafica */}
        <div className="flex items-center justify-center rounded-xl border border-gray-100 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
          <VmDistributionChart counts={counts} />
        </div>
      </div>

      {/* Stato host + utilizzo */}
      <div className="mt-4 grid grid-cols-1 gap-5 md:grid-cols-2">
        <div className="grid grid-cols-3 gap-3">
          <StatCard
            icon={FaTools}
            label="Maintenance mode"
            value={boolLabel(summary?.maintenanceMode, { onTrue: 'Attiva', onFalse: 'No' })}
            accent={summary?.maintenanceMode ? 'text-amber-500' : 'text-emerald-500'}
          />
          <StatCard
            icon={FaSync}
            label="Riavvio richiesto"
            value={boolLabel(summary?.rebootRequired, { onTrue: 'Sì', onFalse: 'No' })}
            accent={summary?.rebootRequired ? 'text-amber-500' : 'text-emerald-500'}
          />
          <StatCard
            icon={FaClock}
            label="Uptime host"
            value={summary?.uptimeSeconds != null ? formatUptime(summary.uptimeSeconds) : 'n/d'}
            accent="text-sky-500"
          />
        </div>

        <div className="flex flex-col justify-center gap-3 rounded-xl border border-gray-100 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
          <UsageBar
            label={
              <span className="inline-flex items-center gap-1.5">
                <FaMicrochip size={12} aria-hidden="true" /> Utilizzo CPU
              </span>
            }
            value={summary?.cpuUsagePct}
            valueLabel={usageLabel(
              summary?.cpuUsagePct,
              summary?.cpuUsageMhz,
              summary?.cpuTotalMhz,
              formatMhz,
            )}
            colorClass="bg-sky-500"
          />
          <UsageBar
            label={
              <span className="inline-flex items-center gap-1.5">
                <FaMemory size={12} aria-hidden="true" /> Utilizzo memoria
              </span>
            }
            value={summary?.memoryUsagePct}
            valueLabel={usageLabel(
              summary?.memoryUsagePct,
              summary?.memoryUsedMb,
              summary?.memoryTotalMb,
              formatMemoryMb,
            )}
            colorClass="bg-primary"
          />
        </div>
      </div>
    </Card>
  );
}
